import React from 'react';
import parse from 'html-react-parser';

const ReadOnlyDocumentContent = ({
  documentComponents,
  classNames = {},
}) => {
  if (!documentComponents || !Object.keys(documentComponents).length) {
    return null;
  }

  const parseDocumentComponent = (documentComponent, documentComponentId) => {
    const unstyledContent = documentComponent.unstyledContent ?? '\u200b';
    const styles = Array.isArray(documentComponent.styling) && documentComponent.styling.length > 0
      ? documentComponent.styling
      : [{ start: 0, end: Math.max(0, unstyledContent.length - 1), types: ['none'] }];
    const htmlText = styles.map((style, index) => {
      const stylingPrefix = style.types.map((type) => {
        switch (type) {
          case 'i':
            return `<i id="${documentComponentId}-${index}">`;
          case 'b':
            return `<b id="${documentComponentId}-${index}">`;
          case 'sup':
            return `<sup id="${documentComponentId}-${index}">`;
          case 'a':
            return `<a id="${documentComponentId}-${index}" href="${style.href}">`;
          default:
            return `<span id="${documentComponentId}-${index}">`;
        }
      }).join("");

      const textContent = unstyledContent.substring(style.start, style.end + 1);

      const stylingPostfix = style.types.map((type) => {
        switch (type) {
          case 'i':
            return '</i>';
          case 'b':
            return '</b>';
          case 'sup':
            return '</sup>';
          case 'a':
            return '</a>';
          default:
            return '</span>';
        }
      }).join("");

      return stylingPrefix + textContent + stylingPostfix;
    }).join("");

    return parse(htmlText, {
      replace: (domNode) => {
        if (domNode.name === 'a' && domNode.attribs) {
          const finalHref = domNode.attribs.href?.includes('http') ? domNode.attribs.href : `//${domNode.attribs.href}`;
          return <a href={finalHref} target="_blank" rel="noopener noreferrer">{domNode.children[0]?.data}</a>;
        }
      }
    });
  };

  const htmlForDocumentComponent = (documentComponent) => {
    switch(documentComponent.documentComponentType) {
      case 'DocumentTitle':
        return <h1 id={documentComponent.id} className={classNames.title} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</h1>;
      case 'DocumentSubtitle':
        return <p id={documentComponent.id} className={classNames.subtitle} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentSectionTitle':
        if (documentComponent.size == 2) {
          return <h2 id={documentComponent.id} className={classNames.sectionTitle} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</h2>;
        }
        return <h3 id={documentComponent.id} className={classNames.sectionTitle} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</h3>;
      case 'DocumentImage':
        const imageSrc = documentComponent?.imagePath;
        return (
          <div className={classNames.imageContainer} key={documentComponent.id}>
            <img id={documentComponent.id} className={classNames.image} src={imageSrc} alt={documentComponent?.altText || ''} />
            {documentComponent?.caption && (
              <p className={classNames.imageCaption}>{documentComponent.caption}</p>
            )}
          </div>
        );
      case 'DocumentParagraph':
        return <p id={documentComponent.id} className={classNames.paragraph} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentEmphasis':
        return <p id={documentComponent.id} className={classNames.emphasis} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentCitationsSection':
        const citations = Object.values(documentComponent.documentCitations)
          .sort((a, b) => a.ordinal - b.ordinal)
          .map((citation) => {
            const fullCitationId = `${documentComponent.id}-${citation.id}`;
            return <li id={fullCitationId} className={classNames.citation} key={citation.id}>{parseDocumentComponent(citation, fullCitationId)}</li>;
          });
        return <ol id={documentComponent.id} key={documentComponent.id}>{citations}</ol>;
      case 'DocumentUnorderedList':
        const listItems = Object.values(documentComponent.documentUnorderedListItems)
          .sort((a, b) => a.ordinal - b.ordinal)
          .map((item) => {
            const fullItemId = `${documentComponent.id}-${item.id}`;
            return <li id={fullItemId} className={classNames.unorderedListItem} key={item.id}>{parseDocumentComponent(item, fullItemId)}</li>;
          });
        return <ul id={documentComponent.id} key={documentComponent.id} className={classNames.unorderedList}>{listItems}</ul>;
      case 'DocumentResumeContactHeader':
        const headerChildren = Object.values(documentComponent.contactHeaderChildren)
          .map((child) => {
            const fullChildId = `${documentComponent.id}-${child.id}`;
            return <div id={fullChildId} className={classNames[`resumeContactHeader_${child.documentComponentType}`]} key={child.id}>{parseDocumentComponent(child, fullChildId)}</div>;
          });
        return <div id={documentComponent.id} key={documentComponent.id} className={classNames.resumeContactHeader}>{headerChildren}</div>;
      case 'DocumentResumeEntryDateRange':
        return <p id={documentComponent.id} className={classNames.resumeEntryDateRange} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      case 'DocumentResumeEntryTitle':
        return <p id={documentComponent.id} className={classNames.resumeEntryTitle} key={documentComponent.id}>{parseDocumentComponent(documentComponent, documentComponent.id)}</p>;
      default:
        return null;
    }
  };

  const sortedDocumentComponents = Object.values(documentComponents).sort((a, b) => a.ordinal - b.ordinal);

  return (
    <div data-testid="read-only-document-content">
      {sortedDocumentComponents.map((documentComponent) => htmlForDocumentComponent(documentComponent))}
    </div>
  );
};

export default ReadOnlyDocumentContent;
